import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, CalendarDays, UserCheck, MapPin, CheckCircle2, Circle } from 'lucide-react';

const fmt = (d) => (d ? format(parseISO(d), 'EEEE d MMMM yyyy', { locale: fr }) : '');
const DEMI = { matin: 'Matin', apres_midi: 'Après-midi', journee: 'Journée' };

// Liste des séances (réutilisée dans l'aperçu admin)
export const FilChronoListe = ({ journees }) => (
  <ol className="relative border-l border-slate-200 ml-3 space-y-3" data-testid="fil-chrono-liste">
    {journees.length === 0 && <li className="ml-4 text-sm text-slate-400">Aucune séance planifiée.</li>}
    {journees.map(j => (
      <li key={`${j.date}-${j.demi_journee}`} className="ml-4" data-testid={`fil-chrono-${j.date}`}>
        <span className={`absolute -left-[7px] mt-1.5 w-3.5 h-3.5 rounded-full border-2 bg-white ${j.realisee ? 'border-cyan-600' : 'border-slate-300'}`} />
        <div className={`rounded-lg border p-3 ${j.realisee ? 'border-cyan-100 bg-cyan-50/40' : 'border-slate-100 bg-white'}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 capitalize">{fmt(j.date)} <span className="text-xs font-normal text-slate-500">· {DEMI[j.demi_journee] || j.demi_journee}{j.type_journee === 'stage' ? ' · Période en entreprise' : ''}</span></p>
            <span className={`text-[11px] inline-flex items-center gap-1 ${j.realisee ? 'text-cyan-700' : 'text-slate-400'}`}>{j.realisee ? <><CheckCircle2 className="w-3.5 h-3.5" /> Réalisée</> : <><Circle className="w-3.5 h-3.5" /> Planifiée</>}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
            {j.formateur && <span className="flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> {j.formateur}</span>}
            {j.lieu && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.lieu}</span>}
          </div>
          {j.modules.length > 0 && <ul className="mt-1.5 text-xs text-slate-700 space-y-0.5">{j.modules.map((m, i) => <li key={i}><span className="font-mono text-[10px] text-slate-400 mr-1">{m.code}</span>{m.titre}</li>)}</ul>}
          {j.deroule && <p className="mt-1.5 text-xs text-slate-600 whitespace-pre-wrap"><span className="font-semibold text-slate-500">Déroulé :</span> {j.deroule}</p>}
        </div>
      </li>
    ))}
  </ol>
);

// Fil chronologique en lecture seule (portails entreprise / financeur)
export const FilChronoDialog = ({ open, onClose, action, apiBase, viewAs }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !action) return;
    setData(null);
    axios.get(`${apiBase}/dossier/${action.dossier_id}/chronologie`, { params: viewAs ? { view_as: viewAs } : {} }).then(r => setData(r.data)).catch(() => setData({ journees: [], erreur: true }));
  }, [open, action, apiBase, viewAs]);
  const journees = data?.journees || [];
  const realisees = journees.filter(j => j.realisee).length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="fil-chrono-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5 text-cyan-600" /> Fil chronologique — {action?.code}</DialogTitle>
          <p className="text-sm text-slate-500">{action?.intitule}{data && ` · ${realisees} séance(s) réalisée(s) / ${journees.length} planifiée(s)`}</p>
        </DialogHeader>
        {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div> : (
          <FilChronoListe journees={journees} />
        )}
      </DialogContent>
    </Dialog>
  );
};
